/**
 * Camera zoom used when BoardCanvas lays out / re-fits the visible document
 * cards (on initial open and on every page change).
 *
 * - **First paint** (`firstPaint`): default to 100%, but **shrink to fit** when
 *   the content is larger than the available area. Capped at 1 so we never zoom
 *   *in* past 100% on small content — that was the intent of #13526 ("default
 *   zoom to 100% instead of fit-to-content"). The bug #13526 introduced was
 *   forcing *exactly* 100%, which let large drawings (or a narrow viewport —
 *   e.g. the V2 chat panel open) overflow the visible band and look
 *   "too zoomed" on the first page. Fitting-down here fixes that while keeping
 *   100% for content that already fits.
 * - **Subsequent re-fit** (page change): fit-to-content, but never exceed the
 *   user's current zoom, so paging doesn't yank a zoomed-in user back out.
 *
 * Pure function so the zoom math is unit-testable independently of the editor /
 * requestAnimationFrame plumbing in BoardCanvas.
 */
export const BOARD_MIN_FIT_ZOOM = 0.1

export interface BoardFitZoomParams {
  /** True on the very first layout for this board/page (no prior fit yet). */
  firstPaint: boolean
  /** Bounding-box width of all document shapes, in page units. */
  contentWidth: number
  /** Bounding-box height of all document shapes, in page units. */
  contentHeight: number
  /** Usable viewport width (already net of toolbar offset + chat reserve). */
  availableWidth: number
  /** Full viewport height in screen px. */
  viewportHeight: number
  /** Padding subtracted from each axis before fitting. */
  inset: number
  /** The editor's current zoom level (editor.getZoomLevel()). */
  currentZoom: number
}

export function computeBoardFitZoom({
  firstPaint,
  contentWidth,
  contentHeight,
  availableWidth,
  viewportHeight,
  inset,
  currentZoom,
}: BoardFitZoomParams): number {
  // Degenerate bounds (e.g. a single zero-size shape) — leave zoom at 100%.
  if (!(contentWidth > 0) || !(contentHeight > 0)) return 1

  const fitWidth = (availableWidth - inset) / contentWidth
  const fitHeight = (viewportHeight - inset) / contentHeight

  if (firstPaint) {
    // 100% by default; zoom OUT to fit oversized content; never IN past 100%.
    return Math.max(BOARD_MIN_FIT_ZOOM, Math.min(1, fitWidth, fitHeight))
  }

  // Page change: fit, but don't undo a user's zoom-in.
  const targetZoom = Math.max(1, currentZoom)
  return Math.max(BOARD_MIN_FIT_ZOOM, Math.min(fitWidth, fitHeight, targetZoom))
}
